import React, { useCallback, useMemo, useState } from "react";

import COLORS from "@appsinti/stats-frontend/resources/constants/colors";
import IChartData, {
  IDataset,
} from "@appsinti/stats-frontend/interfaces/IChartData";
import { CustomCard, LegendContainer, Label } from "../styles";
import Header from "../components/Header";

import useI18n from "@appsinti/i18n/useI18n";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import ChartDataLabels from "chartjs-plugin-datalabels";
import type { ChartData } from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
  zoomPlugin,
  ChartDataLabels
);

import { getOptions } from "./options";
import Loader from "../components/Loader";
import Translation from "@appsinti/i18n/interfaces/Translation";

type BarChart = {
  dataObject: IChartData;
  fullCard?: boolean;
  showLegend?: boolean;
};
const BarChartV2: React.FC<BarChart> = ({
  dataObject,
  fullCard = true,
  showLegend = true,
}) => {
  const { translateObject } = useI18n();
  const [hasData, setHasData] = useState(false);

  const { isOnlyOneColor, colorCallback } = useMemo(() => {
    const withColorCallback = dataObject?.barOptions?.withColorCallback;

    const defaultCallback = (index: number, dataset: IDataset) => {
      console.log({ chart: dataObject.title, dataset });
      if (!dataset.stack) return COLORS[0];

      const stacks = new Set<string>();
      for (const set of dataObject.datasets) {
        if (typeof set.stack === "string") stacks.add(set.stack);
      }
      const stacksArray = Array.from(stacks.values());

      const stackIndex = stacksArray.indexOf(dataset.stack);
      return COLORS[stackIndex % COLORS.length];
    };

    const dataBasedCallback = (index: number, dataset: IDataset) => {
      if (!dataObject) {
        return COLORS[0];
      }
      return COLORS[index];
    };

    const dayBasedCallback = (index: number, dataset: IDataset) => {
      if (!dataObject) {
        return COLORS[0];
      }
      const maxDaysWorked = Math.max(
        ...dataObject.labels.map((label) => {
          const enLabel = (label as Translation)?.en || "";
          const match = enLabel.match(/\((\d+) days\)/);
          return match ? parseInt(match[1], 10) : 0;
        })
      );

      const enLabel = (dataObject?.labels[index] as Translation)?.en || "";
      const match = enLabel.match(/\((\d+) days\)/);
      const days = match ? parseInt(match[1], 10) : 0;
      return days === maxDaysWorked ? COLORS[1] : COLORS[4];
    };

    if (withColorCallback) {
      let callback = defaultCallback;
      if (dataObject.barOptions?.customCallback === "days-based") {
        callback = dayBasedCallback;
      }

      if (dataObject.barOptions?.customCallback === "data-based") {
        callback = dataBasedCallback;
      }

      return {
        isOnlyOneColor: false,
        colorCallback: callback
      };
    } else {
      return {
        isOnlyOneColor: true,
        colorCallback: undefined
      };
    }
  }, [dataObject]);

  const barOptions = useMemo(() => {
    return dataObject?.barOptions || {};
  }, [dataObject]);

  const defineColor = useCallback(
    (index: number, dataset: IDataset, dataObject?: IChartData) => {
      if (!!colorCallback) return colorCallback(index, dataset);
      if (!dataObject || isOnlyOneColor) return COLORS[1];
      return COLORS[
        (COLORS.length -
          ((dataObject.datasets.length - index) % COLORS.length)) %
        COLORS.length
      ];
    },
    [dataObject, isOnlyOneColor, colorCallback]
  );

  const computedBarOptions = useMemo(() => {
    return {
      isStacked: dataObject.stackLabels ? true : false,
      isHorizontal: barOptions.isHorizontal || false,
      yAxis: {
        min: dataObject.yAxis?.min,
        max: dataObject.yAxis?.max,
        showLabels: true,
        ...(barOptions.yAxis || {})
      },
      xAxis: {
        min: dataObject.xAxis?.min,
        max: dataObject.xAxis?.max,
        ...(barOptions.xAxis || {})
      },
      isTimeBased: dataObject.isTimeBased,
      timeOptions: dataObject.timeOptions,
      stackLabels: dataObject.stackLabels || undefined,
      ...barOptions,
      isCurrency: dataObject.isCurrency ?? true,
      isPercentageSet: dataObject.isPercentageData ?? barOptions.isPercentageSet ?? false,
    };
  }, [dataObject, barOptions]);

  const options = useMemo(() => getOptions(computedBarOptions), [computedBarOptions]);

  const data = useMemo(() => {
    if (!dataObject) return null;
    setHasData(
      dataObject.datasets.length > 0 && dataObject.datasets[0].data.length > 0
    );

    const isMultipleDatasets = dataObject.datasets.length > 1;

    if (isMultipleDatasets) {
      const data: ChartData<"bar", number[], string | number> = {
        labels: dataObject.isTimeBased
          ? dataObject.labels
          : dataObject.labels.map((label) => translateObject(label)),
        datasets: dataObject.datasets.map((dataset, index) => ({
          data: dataset.data,
          label:
            typeof dataset.label === "string" ||
              typeof dataset.label === "undefined"
              ? dataset.label
              : translateObject(dataset.label),
          backgroundColor: defineColor(index, dataset),
          borderColor: defineColor(index, dataset),
          stack: dataset.stack,
        })),
      };
      return data;
    }

    const data: ChartData<"bar", number[], string | number> = {
      labels: dataObject.isTimeBased
        ? dataObject.labels
        : dataObject.labels.map((label) => translateObject(label)),
      datasets: dataObject.datasets.map((dataset) => ({
        data: dataset.data,
        backgroundColor: dataset.data.map((_, index) =>
          defineColor(index, dataset, dataObject)
        ),
        borderColor: dataset.data.map((_, index) =>
          defineColor(index, dataset, dataObject)
        ),
        stack: dataset.stack,
      })),
    };

    return data;
  }, [dataObject, defineColor]);

  return fullCard ? (
    <CustomCard
      background="white"
      header={
        <Header
          title={translateObject(dataObject.title)}
          subtitle={dataObject.subtitle && translateObject(dataObject.subtitle)}
          to={dataObject.editLocation}
        />
      }
      body={
        !!data && !!data.datasets ? (
          hasData ? (
            <>
              <Bar options={options} data={data} height={250} />
              {showLegend &&
                (!computedBarOptions.isStacked ||
                  computedBarOptions.stackLabels !== undefined) && (
                  <LegendContainer>
                    {computedBarOptions.isStacked
                      ? data.datasets
                        .filter(
                          (dataset, i, a) =>
                            a.findIndex((t) => t.stack === dataset.stack) === i && dataset.label
                        )
                        .map(
                          (dataset) =>
                            !!computedBarOptions.stackLabels &&
                            !!dataset.stack && (
                              <Label
                                key={dataset.label}
                                color={dataset.borderColor?.toString() ?? ""}
                              >
                                {translateObject(
                                  computedBarOptions.stackLabels[dataset.stack]
                                )}
                              </Label>
                            )
                        )
                      : data.datasets.filter(
                        (dataset) => dataset.label
                      ).map((dataset) => (
                        <Label
                          key={dataset.label}
                          color={dataset.borderColor?.toString() ?? ""}
                        >
                          {dataset.label}
                        </Label>
                      ))}
                  </LegendContainer>
                )}
            </>
          ) : (
            <h3 style={{ textAlign: "center" }}>No Data</h3>
          )
        ) : (
          <Loader />
        )
      }
    />
  ) : (
    data && <Bar options={options} data={data} height={250} />
  );
};

export default BarChartV2;
